/**
 * Drawing a sentiment score (S08).
 *
 * The score runs −1 to +1 and the bar is centred, so a negative score grows
 * leftwards from the middle and a positive one rightwards. That makes the
 * geometry two values rather than one: how wide the fill is, and where it
 * starts. Getting `left` wrong puts a hostile call's bar on the friendly side,
 * which is the kind of mistake that reads as correct until someone checks.
 */

/** Below this, in either direction, the score is not saying much. */
export const NEUTRAL_BAND = 0.15;

export interface BarGeometry {
  /** Percentage of the track's full width. */
  width: number;
  /** Percentage offset of the fill's left edge. */
  left: number;
  /** Which side of neutral this falls on, for colouring. */
  tone: 'positive' | 'negative' | 'neutral';
}

/**
 * Where the fill sits for a given score.
 *
 * Half the track is one polarity, so a full ±1 fills half the bar — not all of
 * it. A bar that reached the end at +1 would leave nothing to distinguish
 * "strongly positive" from "as positive as the scale goes".
 */
export function barGeometry(score: number): BarGeometry {
  const clamped = Math.max(-1, Math.min(1, safe(score)));
  const width = Math.abs(clamped) * 50;
  return {
    width,
    left: clamped >= 0 ? 50 : 50 - width,
    tone: toneOf(clamped),
  };
}

export function toneOf(score: number): 'positive' | 'negative' | 'neutral' {
  const value = safe(score);
  if (value > NEUTRAL_BAND) return 'positive';
  if (value < -NEUTRAL_BAND) return 'negative';
  return 'neutral';
}

/**
 * The score as an operator reads it: two decimals, and a sign when positive.
 *
 * The leading `+` is not decoration. Without it `0.72` and `-0.72` differ by one
 * glyph at the far left of a number, which is exactly where a quick scan does
 * not look.
 */
export function formatScore(score: number): string {
  const value = Math.max(-1, Math.min(1, safe(score)));
  const shown = value.toFixed(2);
  return value > 0 ? `+${shown}` : shown;
}

/** Magnitude is 0–1 and fills from the left, so it needs no offset. */
export function magnitudePercent(magnitude: number): number {
  return Math.round(Math.max(0, Math.min(1, safe(magnitude))) * 100);
}

/**
 * A number, or zero.
 *
 * The analyzer's output is mapped before it is stored, so this should never
 * fire — but a NaN reaching CSS produces `width: NaNpx`, which renders as a bar
 * of no width and looks like a genuine reading of nothing.
 */
function safe(value: number): number {
  return Number.isFinite(value) ? value : 0;
}
